export const MAX_EDGE = 1600
export const QUALITY = 0.82

/**
 * Re-encodes an image through a canvas.
 *
 * Two things fall out of that, both required by architecture section 7:
 * a 12 MP phone photo drops from 4-6 MB to roughly 300 KB, and ALL EXIF
 * metadata is discarded -- including the GPS coordinates a phone embeds,
 * which would otherwise mean a clinical photo carries the patient's
 * location with it.
 */
export async function resizeAndStrip(source: Blob | HTMLVideoElement): Promise<Blob> {
  const { width, height, draw } = await readSource(source)
  if (!width || !height) {
    throw new Error('That image has no readable dimensions. Try capturing it again.')
  }
  const scale = Math.min(1, MAX_EDGE / Math.max(width, height))
  const w = Math.round(width * scale)
  const h = Math.round(height * scale)

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas is unavailable in this browser')
  ctx.drawImage(draw as CanvasImageSource, 0, 0, w, h)

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      b => (b ? resolve(b) : reject(new Error('Could not encode the image'))),
      'image/jpeg',
      QUALITY,
    )
  })
}

async function readSource(source: Blob | HTMLVideoElement) {
  if (source instanceof HTMLVideoElement) {
    return { width: source.videoWidth, height: source.videoHeight, draw: source }
  }
  const bitmap = await createImageBitmap(source)
  return { width: bitmap.width, height: bitmap.height, draw: bitmap }
}

/** Canvas contents as a JPEG, used for the signature pad. */
export function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      b => (b ? resolve(b) : reject(new Error('Could not encode the signature'))),
      'image/png',
    )
  })
}

export const humanSize = (bytes: number) =>
  bytes < 1024 ? `${bytes} B`
  : bytes < 1024 * 1024 ? `${Math.round(bytes / 1024)} KB`
  : `${(bytes / 1024 / 1024).toFixed(1)} MB`
